import {useEffect, useMemo} from 'react';
import {UiCard, UiNotice, UiStatus, UiTabs, type UiTabItem} from '@morefunos/ui';
import {ActiveWorkPage} from './ActiveWorkPage.tsx';
import {OrderingPage} from './OrderingPage.tsx';
import {PlaceholderPage} from './PlaceholderPage.tsx';
import {createSmmActiveWorkPort, type SmmActiveWorkPortConfig} from './smm-active-work-port.ts';
import {createSmmOrderingPort} from './smm-ordering-port.ts';
import {resolveSmmRuntimeHost, type SmmRuntimeHost} from './runtime.ts';

export interface AppProps {
  readonly runtimeHost?: SmmRuntimeHost | null;
}

const navLabel = (symbol: string, label: string) => (
  <span className="smm-tab-label">
    <span className="smm-tab-symbol" aria-hidden="true">{symbol}</span>
    <span>{label}</span>
  </span>
);

function RuntimeBlocked() {
  return (
    <section className="smm-page" aria-labelledby="smm-runtime-blocked-title">
      <div className="smm-section-heading">
        <p className="smm-eyebrow">Fail closed</p>
        <h1 id="smm-runtime-blocked-title">待處理</h1>
      </div>
      <UiCard className="smm-placeholder-card">
        <UiNotice tone="critical" role="alert">Runtime 未就緒</UiNotice>
        <p className="smm-muted">
          未收到正式 Business Runtime、Catalog、businessDate、operatorId 同 deviceId，SMM 唔會建立假 workspace 或顯示假待處理資料。
        </p>
      </UiCard>
    </section>
  );
}

export function App({runtimeHost}: AppProps) {
  const host = runtimeHost === undefined ? resolveSmmRuntimeHost() : runtimeHost;

  const ports = useMemo(() => {
    if (!host) return null;
    let sequence = 0;
    const nextId: SmmActiveWorkPortConfig['nextId'] = (kind) => {
      const random = globalThis.crypto?.randomUUID?.();
      return `${kind}:${random ?? `${Date.now()}:${++sequence}`}`;
    };
    const activeWork = createSmmActiveWorkPort({
      runtime: host.runtime,
      operatorId: host.operatorId,
      deviceId: host.deviceId,
      ...(host.sessionRef ? {sessionRef: host.sessionRef} : {}),
      now: () => new Date().toISOString(),
      nextId,
      initialServiceMode: 'takeaway',
    });
    const ordering = createSmmOrderingPort({
      runtime: host.runtime,
      catalog: host.catalog,
      activeWork,
      operatorId: host.operatorId,
      deviceId: host.deviceId,
      ...(host.sessionRef ? {sessionRef: host.sessionRef} : {}),
      businessDate: host.businessDate,
      now: () => new Date().toISOString(),
      nextId,
    });
    return {activeWork, ordering};
  }, [host]);

  useEffect(() => {
    const root = document.documentElement;
    const updateViewport = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      root.style.setProperty('--smm-viewport-height', `${Math.max(1, height)}px`);
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);
    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
    };
  }, []);

  const items: readonly UiTabItem[] = [
    {
      id: 'order',
      label: navLabel('＋', '點單'),
      'aria-label': '點單',
      content: <OrderingPage port={ports?.ordering ?? null} />,
    },
    {
      id: 'active',
      label: navLabel('◎', '待處理'),
      'aria-label': '待處理',
      content: ports ? <ActiveWorkPage port={ports.activeWork} /> : <RuntimeBlocked />,
    },
    {
      id: 'orders',
      label: navLabel('▤', '訂單'),
      'aria-label': '訂單',
      content: (
        <PlaceholderPage
          title="訂單"
          description="訂單功能尚未接入目前流動端；正式能力接入前不會顯示假資料。"
        />
      ),
    },
    {
      id: 'more',
      label: navLabel('•••', '更多'),
      'aria-label': '更多',
      content: (
        <PlaceholderPage
          title="更多"
          description="售罄、堂食、Business Day、Reporting 等模組按證據逐個接入，唔會喺 UI 先自創權限。"
        />
      ),
    },
  ];

  return (
    <main className="smm-app-shell">
      <header className="smm-topbar">
        <div className="smm-brand-mark" aria-hidden="true">磨</div>
        <div className="smm-brand-copy">
          <strong>MoreFun SMM</strong>
          <span>{host ? `${host.operatorId} · ${host.deviceId}` : '等待 Runtime 接入'}</span>
        </div>
        <UiStatus tone={host ? 'positive' : 'critical'}>{host ? 'Business Runtime' : '未就緒'}</UiStatus>
      </header>

      {!host ? (
        <UiNotice tone="critical" role="alert" className="smm-global-runtime-alert">
          Runtime 未就緒｜目前只顯示 SMM UI shell；所有 Business action fail closed。
        </UiNotice>
      ) : null}

      <UiTabs label="SMM 主功能" items={items} defaultSelectedKey="active" />
    </main>
  );
}
